/** @jsxImportSource @compiled/react */
import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- React must be in scope for JSX in Jest
void React;

import { defineMessages, useIntl } from 'react-intl';
import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { Anchor, Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SpaceContributor } from './useSpaceContributors.ts';

const i18n = defineMessages({
	created: {
		id: 'space-contributors.card.created',
		defaultMessage: 'Created {count, plural, one {# page} other {# pages}}',
		description: 'Number of pages created by a contributor',
	},
	edited: {
		id: 'space-contributors.card.edited',
		defaultMessage: 'Edited {count, plural, one {# page} other {# pages}}',
		description: 'Number of pages edited by a contributor',
	},
});

export interface ContributorCardProps {
	contributor: SpaceContributor;
}

const styles = cssMap({
	card: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: token('space.200'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.raised'),
		boxShadow: token('elevation.shadow.raised'),
		textAlign: 'center',
		minWidth: '120px',
	},
	nameLink: {
		textDecoration: 'none',
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
	},
});

export const ContributorCard = ({ contributor }: ContributorCardProps) => {
	const intl = useIntl();
	const profileUrl = `/wiki/people/${contributor.accountId}`;
	return (
		<Box xcss={styles.card} testId={`contributor-card-${contributor.accountId}`}>
			<Stack alignInline="center" space="space.100">
				<Avatar
					name={contributor.displayName}
					src={contributor.avatarUrl}
					href={profileUrl}
					target="_blank"
					size="large"
				/>
				<Anchor href={profileUrl} target="_blank" xcss={styles.nameLink}>
					{contributor.displayName}
				</Anchor>
				{(contributor.created ?? 0) > 0 && (
					<Text color="color.text.subtlest" size="small">
						{intl.formatMessage(i18n.created, { count: contributor.created })}
					</Text>
				)}
				{(contributor.updated ?? 0) > 0 && (
					<Text color="color.text.subtlest" size="small">
						{intl.formatMessage(i18n.edited, { count: contributor.updated })}
					</Text>
				)}
			</Stack>
		</Box>
	);
};
